import { useState } from 'react';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import { useAuth } from '../contexts/AuthContext';
import { saveProgressReport } from '../services/progressReports';
import BackButton from './BackButton';
import { useUserIdentity } from '../hooks/useUserIdentity';
import ButtonSpinner from './ButtonSpinner';
import { busyStyle } from '../utils/busyStyle';

interface CoachProgressReportProps {
  order: Order;
  /** The final lesson (lessonNumber === totalLessons) */
  finalLesson: Lesson;
  onBack?: () => void;
  onSubmitted?: () => void;
}

export default function CoachProgressReport({ order, finalLesson, onBack, onSubmitted }: CoachProgressReportProps) {
  const { currentUser } = useAuth();

  const [whatImproved, setWhatImproved] = useState('');
  const [keepDoing, setKeepDoing] = useState('');
  const [nextFocus, setNextFocus] = useState('');
  const [personalNote, setPersonalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const livePlayer = useUserIdentity(order.playerId, 'player');
  const playerName = livePlayer?.name ?? order.playerName ?? 'Player';
  const playerFirstName = playerName.split(' ')[0];
  const totalLessons =order.totalLessons ?? 4;

  const enrolledDate = order.createdAt
    ? new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const deliveredDate = finalLesson.deliveredAt
    ? new Date(finalLesson.deliveredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';


  const canSubmit = whatImproved.trim() && keepDoing.trim() && nextFocus.trim();

  const handleSubmit = async () => {
    if (!currentUser || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await saveProgressReport({
        coachId: currentUser.uid,
        playerId: order.playerId,
        orderId: order.id,
        lessonId: finalLesson.id,
        whatImproved: whatImproved.trim(),
        keepDoing: keepDoing.trim(),
        nextFocus: nextFocus.trim(),
        personalNote: personalNote.trim() || null,
      });
      onSubmitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report.');
      setSubmitting(false);
    }
  };

  const textareaClass = "w-full rounded-lg px-3.5 py-[11px] text-[16px] leading-[21px] text-db-text-1 placeholder:text-db-text-3 resize-y focus:outline-none transition-colors input-glow";
  const textareaStyle = { border: '1.5px solid #E4E4E2', fontFamily: 'inherit' };

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-db-bg">
      <div className="w-full mx-auto px-4 py-6 md:px-[40px] md:py-[36px]" style={{ maxWidth: '720px' }}>

        <BackButton onClick={onBack} fallback="/orders" size="sm" sticky />

        {/* Navy header card */}
        <div className="rounded-[12px] px-5 py-5 md:px-7 md:py-6 mb-7 flex items-center justify-between gap-4 bg-biscayne">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[.08em] mb-1.5" style={{ color: 'rgba(255,255,255,.45)' }}>
              Training Program · Lesson {totalLessons} of {totalLessons}
            </div>
            <div className="text-[22px] font-extrabold text-white mb-1">{playerName}</div>
            <div className="text-[13px]" style={{ color: 'rgba(255,255,255,.55)' }}>
              {enrolledDate ? `Enrolled ${enrolledDate}` : ''}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span
              className="text-[11px] font-extrabold uppercase rounded-full whitespace-nowrap"
              style={{ color: 'white', padding: '5px 14px', letterSpacing: '.05em' }}
            >
              Final Lesson
            </span>
            {deliveredDate && (
              <div className="text-[11px] mt-2" style={{ color: 'rgba(255,255,255,.5)' }}>Delivered {deliveredDate}</div>
            )}
          </div>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-[12px] px-5 py-6 md:px-8 md:py-7 mb-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
          <div className="mb-7">
            <div className="text-[18px] font-extrabold text-db-text-1 mb-1">Write Progress Report</div>
            <div className="text-[13px] leading-relaxed text-db-text-1">
              Write directly to {playerFirstName}. They'll receive this as part of their program summary. Players share these, so take your time.
            </div>
          </div>

          <div className="space-y-[22px]">
            {/* Field 1 */}
            <div>
              <label className="block text-[13px] font-bold text-db-text-1 mb-[7px]">
                What improved the most? <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <textarea
                value={whatImproved}
                onChange={e => setWhatImproved(e.target.value)}
                rows={3}
                placeholder={`e.g. Your racket-drop timing is night and day from lesson 1...`}
                className={textareaClass}
                style={textareaStyle}
                onFocus={e => { e.target.style.borderColor = '#1A6B8A'; }}
                onBlur={e => { e.target.style.borderColor = '#E4E4E2'; }}
              />
            </div>

            {/* Field 2 */}
            <div>
              <label className="block text-[13px] font-bold text-db-text-1 mb-[7px]">
                What should {playerFirstName} keep doing? <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <textarea
                value={keepDoing}
                onChange={e => setKeepDoing(e.target.value)}
                rows={3}
                placeholder={`e.g. Your shadow-swing drill is clearly paying off. Your unit turn is more complete now...`}
                className={textareaClass}
                style={textareaStyle}
                onFocus={e => { e.target.style.borderColor = '#1A6B8A'; }}
                onBlur={e => { e.target.style.borderColor = '#E4E4E2'; }}
              />
            </div>

            {/* Field 3 */}
            <div>
              <label className="block text-[13px] font-bold text-db-text-1 mb-[7px]">
                What would you work on next with {playerFirstName}? <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <textarea
                value={nextFocus}
                onChange={e => setNextFocus(e.target.value)}
                rows={3}
                placeholder={`e.g. Your second serve under pressure is the clear next frontier...`}
                className={textareaClass}
                style={textareaStyle}
                onFocus={e => { e.target.style.borderColor = '#1A6B8A'; }}
                onBlur={e => { e.target.style.borderColor = '#E4E4E2'; }}
              />
            </div>

            {/* Field 4 */}
            <div>
              <label className="block text-[13px] font-bold text-db-text-1 mb-[7px]">
                Personal note to {playerFirstName} <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <textarea
                value={personalNote}
                onChange={e => setPersonalNote(e.target.value)}
                rows={3}
                placeholder={`e.g. ${playerFirstName}, the way you've committed over these ${totalLessons} lessons tells me you're going to hit your goals...`}
                className={textareaClass}
                style={textareaStyle}
                onFocus={e => { e.target.style.borderColor = '#1A6B8A'; }}
                onBlur={e => { e.target.style.borderColor = '#E4E4E2'; }}
              />
            </div>
          </div>

          {error && (
            <div className="text-[12px] text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-4">{error}</div>
          )}
        </div>

        {/* Submit row */}
        <div className="flex items-center justify-between gap-4">
          <div className="text-[12px] text-db-text-3">
            {playerFirstName} will see this as soon as you submit.
          </div>
          <button
            aria-busy={submitting}
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="flex items-center gap-2 text-[14px] font-bold text-white px-7 py-3 rounded-lg disabled:opacity-50 transition-colors whitespace-nowrap flex-shrink-0 bg-biscayne btn-press"
            style={busyStyle(submitting)}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {submitting ? <ButtonSpinner /> : 'Submit Report'}
          </button>
        </div>

      </div>
    </div>
  );
}
